import { routeKeys } from "@/api/Routes";
import { activityKeys } from "@/api/Activities";
import { useEventSource } from "@/Utils/sse";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { patientsWithUnhandledMeasurementsQueryOptions } from "@/api/Patients";
import { shiftKeys } from "@/api/Shifts";

const AGGREGATE_CHANGED_SSE = "aggregateChanged";

// These keys are the parents of every known logistics query, dealing with activities, routes etc.
const ALL_KNOWN_LOGISTICS_QUERY_KEYS = [
  routeKeys.all,
  activityKeys.all,
  shiftKeys.listWithContents({}),
];

export const useCenterLogisticsSSEHandlers = () => {
  const queryClient = useQueryClient();
  // https://react.dev/reference/react/useMemo#caveats says to prefer useState for safely persisting state between renders: https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
  const [eventHandlers] = useState<Parameters<typeof useEventSource>[1]>(() => [
    {
      relevantEvents: [AGGREGATE_CHANGED_SSE],
      handler: () => {
        ALL_KNOWN_LOGISTICS_QUERY_KEYS.forEach((queryKey) => {
          void queryClient.invalidateQueries({ queryKey });
        });
      },
    },
  ]);
  useEventSource("logistics", eventHandlers);
};

export const useCenterPatientSSEHandlers = () => {
  const queryClient = useQueryClient();
  // https://react.dev/reference/react/useMemo#caveats says to prefer useState for safely persisting state between renders: https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
  const [eventHandlers] = useState(() => [
    // Ensure unhandled measurements-collection is refreshed when measurements are changed
    {
      relevantEvents: [AGGREGATE_CHANGED_SSE],
      handler: () => {
        void queryClient.invalidateQueries({
          queryKey: patientsWithUnhandledMeasurementsQueryOptions.queryKey,
        });
      },
    },
  ]);
  useEventSource("patient", eventHandlers);
};
